import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
  Container, Grid, GridList, GridListTile
} from '@material-ui/core';
import moment from 'moment';
import Page from 'src/components/Page';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import AssignmentIcon from '@material-ui/icons/Assignment';
import MobileFriendlyIcon from '@material-ui/icons/MobileFriendly';
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import HowToRegIcon from '@material-ui/icons/HowToReg';
import { selectCompany } from 'src/actions/company/companySelectors';
import Header from './Header';
import OverviewBox from './OverviewBox';
import TransfersOverTime from './TransfersOverTime';
import TransferAmounts from './TransferAmounts';
import Usage from './Usage';
import { useToasts } from '../../utils/Toast';
import { displayErrorMessage } from '../../utils/messages';
import axios from 'src/utils/axios';
import Sync from '@material-ui/icons/Sync';
import i18next from 'i18next';
import { numberToCountryCurrency } from 'src/locales/localesUtils';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    marginBottom: theme.spacing(8)
  },
  grid: {
    marginTop: theme.spacing(2)
  },
  rotateIcon: {
    animation: 'spin 4s linear infinite',
    fontSize: '40px'
  }
}));

const DashboardDefault = () => {
  const { error: errorToast } = useToasts();
  const classes = useStyles();
  const [historicData, setHistoricData] = useState(null);
  const [periodData, setPeriodData] = useState(null);
  const [chartsData, setChartsData] = useState([]);
  const [period, setPeriod] = useState();
  const currentCompany = useSelector(selectCompany);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        let metrics;
        if(!historicData)
          metrics = await axios.get(`${process.env.REACT_APP_API_URL}metrics?historic=true&aggregation=${period}`);
        else
          metrics = await axios.get(`${process.env.REACT_APP_API_URL}metrics?aggregation=${period}`);

        if (mounted) {
          if(!historicData)
            setHistoricData({...metrics.data.historicMetrics});
          setPeriodData({...metrics.data.byPeriodMetrics});
          setChartsData(metrics.data.chartsData);
        }
      } catch (e) {
        const requsetType = 'GET';
        displayErrorMessage(errorToast)(e, requsetType);
      }
    };

    if(period) fetchData();
    
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    if(currentCompany){
      const isDeferred = currentCompany?.settings?.paymentFrequency === 'monthly (deferred)';
      const companyCountry  = currentCompany.countryCode;
      setPeriod(isDeferred ?'period' :'month');
      i18next.changeLanguage(companyCountry);
    }
  }, [currentCompany]);

  const companyCreatedAtDate = currentCompany
    && moment(currentCompany?.createdAt)
      .local()
      .format('YYYY-MM-DD HH:m:s');
  const formatBoxTitle = companyCreatedAtDate && `Desde ${companyCreatedAtDate}`;

  if(!historicData)
    return(
      <>
        <Sync className={ classes.rotateIcon } />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(360deg); }
            100% { transform: rotate(0deg); }
          }
          `}
        </style>
      </>
    )

  return (
    <Page className={classes.root} title="Resumen">
      <Container maxWidth={false}>
        <Header setPeriod={setPeriod} period={period} />
        <Grid container spacing={3} className={classes.grid}>
          <Grid container item spacing={3} lg={12}>
            <Grid item lg={4}>
              <OverviewBox
                headline="Total están registrados"
                title={formatBoxTitle}
                amount={historicData?.registeredEmployees}
                icon={<HowToRegIcon />}
                className="light-blue"
              />
            </Grid>
            <Grid item lg={4}>
              <OverviewBox
                headline="Total activos"
                title={formatBoxTitle}
                amount={historicData?.validEmployees}
                icon={<SmartphoneIcon />}
                className="light-blue"
              />
            </Grid>
            <Grid item lg={4}>
              <OverviewBox
                headline="Total lo han utilizado"
                title={formatBoxTitle}
                amount={historicData?.employeesWithUsage}
                icon={<MobileFriendlyIcon />}
                className="light-blue"
              />
            </Grid>
          </Grid>
          <Grid container item spacing={3} lg={12}>
            <Grid item lg={8} xs={12}>
              <Usage
                newEmployeesByPeriod={historicData?.percentageRegistered}
                newPaymentsByPeriod={periodData?.percentageWithUsageInPeriod}
                period={period}
              />
            </Grid>
            <Grid item lg={4} xs={12}>
              <GridList cellHeight={133} cols={1}>
                <GridListTile>
                  <OverviewBox
                    headline="Total solicitado"
                    amount={`${numberToCountryCurrency(periodData?.totalWithdrawns, currentCompany.countryCode)}`}
                    icon={<SwapHorizIcon />}
                    className="background-gradient-blue"
                  />
                </GridListTile>
                <GridListTile>
                  <OverviewBox
                    headline="Monto medio"
                    amount={`${numberToCountryCurrency(Math.round(periodData?.averageWithdrawns), currentCompany.countryCode)}`}
                    icon={<AssignmentIndIcon />}
                    className="background-gradient-green"
                  />
                </GridListTile>
                <GridListTile>
                  <OverviewBox
                    headline="N° de solicitudes"
                    amount={periodData?.countWithdrawals}
                    icon={<AssignmentIcon />}
                    className="light-blue"
                  />
                </GridListTile>
              </GridList>
            </Grid>
          </Grid>
          <Grid item lg={12}>
            <TransfersOverTime data={chartsData} />
          </Grid>
          <Grid item lg={12}>
            <TransferAmounts data={chartsData} />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default DashboardDefault;
